import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { hubApi } from '../../lib/hubApi';
import { ConfirmModal } from '../../components/shared/ConfirmModal';
import { HubPageHeader } from '../../components/hub/HubPageHeader';

type Trigger = { severity?: 'high' | 'medium' | 'low' | 'info'; category?: string };
type Channel = { kind: 'email' | 'slack'; target: string };
type Schedule = 'realtime' | 'daily' | 'weekly';

type Subscription = {
  id: string;
  name: string;
  triggers: Trigger[];
  channels: Channel[];
  schedule: Schedule;
  paused: boolean;
  createdAt: number;
};

export function HubAlertsSubscriptionPage() {
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await hubApi.get<{ subscriptions: Subscription[] }>('/subscriptions');
      setSubs(data.subscriptions);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <section className="space-y-6 max-w-4xl">
      <HubPageHeader
        title={
          <span className="inline-flex items-center gap-2.5">
            Alert subscriptions
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-warning/15 border border-warning/40 text-warning uppercase tracking-wider">
              POC · Day 2
            </span>
          </span>
        }
        subtitle="Get notified when new findings match. Audits run weekly; manual re-runs trigger immediate checks."
        actions={
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="px-3.5 py-2 rounded-md bg-sfblue hover:bg-sfblue/85 text-sm font-medium text-white transition-colors"
          >
            + New subscription
          </button>
        }
      />

      <div className="px-4 py-3 rounded-lg border border-warning/40 bg-warning/10 text-sm text-warning">
        <strong className="font-semibold">POC note.</strong> The page is real; the plumbing
        isn&apos;t. For Day 1 the UI persists subscriptions, but actual alert sending depends on the
        Day 2 audit scheduler + change-detection pipeline. Channels (email/Slack) require
        integrations we haven&apos;t built yet.
      </div>

      {creating && (
        <CreateSubscriptionForm
          onCancel={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false);
            await refresh();
          }}
        />
      )}

      {loading ? (
        <p className="text-text-muted text-sm">Loading…</p>
      ) : subs.length === 0 ? (
        <p className="text-text-muted text-sm">No subscriptions yet.</p>
      ) : (
        <div className="flex flex-col gap-2.5">
          {subs.map((s) => (
            <SubscriptionRow key={s.id} sub={s} onChange={refresh} />
          ))}
        </div>
      )}
    </section>
  );
}

function SubscriptionRow({ sub, onChange }: { sub: Subscription; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);

  const togglePause = async () => {
    setBusy(true);
    try {
      await hubApi.post(`/subscriptions/${sub.id}/pause`, { paused: !sub.paused });
      onChange();
    } catch (err) {
      const m = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      toast.error(m ?? (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await hubApi.delete(`/subscriptions/${sub.id}`);
      setPendingDelete(false);
      onChange();
    } catch (err) {
      const m = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      toast.error(m ?? (err as Error).message);
      setPendingDelete(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-border-subtle bg-surface-2 shadow-card p-4 transition-all hover:border-border-strong">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm text-text-strong">{sub.name}</div>
          <div className="text-xs text-text-muted mt-1.5">
            <span className="text-text-subtle">Triggers:</span>{' '}
            {sub.triggers
              .map((t) => [t.severity, t.category].filter(Boolean).join(' / '))
              .join(', ') || 'any finding'}
          </div>
          <div className="text-xs text-text-muted mt-0.5">
            <span className="text-text-subtle">Channels:</span>{' '}
            {sub.channels.map((c) => `${c.kind}: ${c.target}`).join(', ')}
          </div>
          <div className="text-xs text-text-muted mt-0.5">
            <span className="text-text-subtle">Schedule:</span> {sub.schedule}
          </div>
        </div>
        <div className="flex flex-col gap-1.5 text-xs">
          <button
            type="button"
            onClick={togglePause}
            disabled={busy}
            className="px-2.5 py-1 rounded-md border border-border bg-surface-1 text-text hover:bg-surface-hover hover:border-border-strong disabled:opacity-50 transition-colors"
          >
            {sub.paused ? 'Resume' : 'Pause'}
          </button>
          <button
            type="button"
            onClick={() => setPendingDelete(true)}
            disabled={busy}
            className="px-2.5 py-1 rounded-md border border-danger/40 bg-danger/10 text-danger hover:bg-danger/15 hover:border-danger/60 disabled:opacity-50 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
      <ConfirmModal
        open={pendingDelete}
        title={`Delete "${sub.name}"?`}
        body="This removes the subscription permanently. Existing alerts already sent are unaffected."
        confirmLabel="Delete"
        reversibilityNote="Irreversible. You'll need to recreate the subscription to resume alerts."
        onCancel={() => setPendingDelete(false)}
        onConfirm={remove}
      />
    </div>
  );
}

function CreateSubscriptionForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: () => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [severity, setSeverity] = useState<'all' | 'high' | 'medium' | 'low' | 'info'>('high');
  const [emailTarget, setEmailTarget] = useState('');
  const [slackTarget, setSlackTarget] = useState('');
  const [schedule, setSchedule] = useState<Schedule>('realtime');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!name.trim() || (!emailTarget.trim() && !slackTarget.trim())) {
      toast.error('Name + at least one channel required');
      return;
    }
    setSubmitting(true);
    try {
      const triggers: Trigger[] = [];
      // 'all' is a UI-only sentinel — sent as an empty triggers[] meaning "match any finding".
      if (severity !== 'all') triggers.push({ severity });
      const channels: Channel[] = [];
      if (emailTarget.trim()) channels.push({ kind: 'email', target: emailTarget.trim() });
      if (slackTarget.trim()) channels.push({ kind: 'slack', target: slackTarget.trim() });

      await hubApi.post('/subscriptions', { name, triggers, channels, schedule });
      toast.success('Subscription created');
      await onCreated();
    } catch (err) {
      const m = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      toast.error(m ?? (err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'mt-1.5 w-full px-3 py-2 rounded-md border border-border bg-surface-1 text-sm text-text placeholder:text-text-subtle focus:outline-none focus:border-border-strong';
  const labelClass = 'text-[10px] font-semibold uppercase tracking-[0.14em] text-text-subtle';

  return (
    <div className="rounded-lg border border-border-subtle bg-surface-2 shadow-card p-5 space-y-4 text-sm">
      <label className="block">
        <span className={labelClass}>Name</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. High-severity Permissions findings"
          className={inputClass}
        />
      </label>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className={labelClass}>Trigger severity</span>
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value as typeof severity)}
            className={inputClass}
          >
            <option value="all">All</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="info">Info</option>
          </select>
        </label>
        <label className="block">
          <span className={labelClass}>Schedule</span>
          <select
            value={schedule}
            onChange={(e) => setSchedule(e.target.value as Schedule)}
            className={inputClass}
          >
            <option value="realtime">Real-time</option>
            <option value="daily">Daily digest</option>
            <option value="weekly">Weekly digest</option>
          </select>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className={labelClass}>Email (optional)</span>
          <input
            value={emailTarget}
            onChange={(e) => setEmailTarget(e.target.value)}
            placeholder="ops@acme.com"
            className={inputClass}
          />
        </label>
        <label className="block">
          <span className={labelClass}>Slack channel (optional)</span>
          <input
            value={slackTarget}
            onChange={(e) => setSlackTarget(e.target.value)}
            placeholder="#salesforce-ops"
            className={inputClass}
          />
        </label>
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t border-border-subtle">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded-md border border-border bg-surface-1 text-text text-xs hover:bg-surface-hover hover:border-border-strong transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={submit}
          className="px-3.5 py-1.5 rounded-md bg-sfblue hover:bg-sfblue/85 text-xs font-medium text-white disabled:opacity-50 transition-colors"
        >
          {submitting ? 'Creating…' : 'Create subscription'}
        </button>
      </div>
    </div>
  );
}
